import React, { useMemo, useState } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import Button from '../../components/atoms/Buttons'
import { GET_AVAILABILITY } from '../../apollo/queries/booking'
import { UPDATE_BOOKING } from '../../apollo/mutations/booking'
import './style.css'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(advancedFormat)

const RescheduleBookingModal = ({ booking, event, onClose, onDone }) => {
  const zone = event?.timezone || 'America/Chicago'
  const minDate = useMemo(
    () => (event?.startDate ? event.startDate.slice(0, 10) : dayjs().format('YYYY-MM-DD')),
    [event]
  )
  const maxDate = useMemo(
    () => (event?.endDate ? event.endDate.slice(0, 10) : minDate),
    [event, minDate]
  )

  const [date, setDate] = useState(dayjs(booking.startTime).tz(zone).format('YYYY-MM-DD'))
  const [selectedSlot, setSelectedSlot] = useState('')
  const [errorText, setErrorText] = useState('')

  const { data: avail, loading: availLoading } = useQuery(GET_AVAILABILITY, {
    variables: { eventId: booking.eventId, date },
    skip: !date,
    fetchPolicy: 'network-only',
  })
  const slots = avail?.getAvailability || []

  const [updateBooking, { loading: saving }] = useMutation(UPDATE_BOOKING, {
    refetchQueries: ['listBookingsByDate'],
  })

  const fmtTime = (iso) => dayjs(iso).tz(zone).format('h:mm A')

  const submit = async () => {
    if (!selectedSlot) {
      setErrorText('Pick a new time first.')
      return
    }
    try {
      await updateBooking({ variables: { id: booking.id, input: { startTime: selectedSlot } } })
      onDone?.()
    } catch (err) {
      setErrorText(err?.message || 'Could not reschedule.')
    }
  }

  return (
    <div>
      <div className="booking-modal-backdrop" onClick={onClose} />
      <div className="booking-modal-container" style={{ maxWidth: 520, width: '92%' }}>
        <h2 className="add-events !text-[28px] !mt-0 mb-1">Reschedule Booking</h2>
        <div className="text-[14px] text-[color:var(--color-secondary)] mb-1">{event?.eventName}</div>
        <div className="text-[12px] font-montserrat text-[color:var(--color-gray-text)] mb-4">
          Currently: {dayjs(booking.startTime).tz(zone).format('dddd, MMM D · h:mm A z')}
        </div>

        <div className="space-y-4">
          <div>
            <div className="booking-form-label">Pick a new day</div>
            <input
              type="date"
              className="booking-form-field"
              value={date}
              min={minDate}
              max={maxDate}
              onChange={(e) => { setDate(e.target.value); setSelectedSlot('') }}
            />
          </div>
          <div>
            <div className="booking-form-label">Choose a time</div>
            {availLoading ? (
              <div className="text-[12px] mt-2">Loading times…</div>
            ) : slots.length === 0 ? (
              <div className="text-[13px] mt-2 text-[color:var(--color-gray-text)]">No openings that day.</div>
            ) : (
              <div className="grid grid-cols-4 gap-2 mt-2">
                {slots.map((s) => (
                  <button
                    type="button"
                    key={s.startTime}
                    onClick={() => setSelectedSlot(s.startTime)}
                    className={`h-[40px] rounded-[8px] border text-[13px] ${
                      selectedSlot === s.startTime
                        ? 'bg-[color:var(--color-primary)] text-white border-[color:var(--color-primary)]'
                        : 'bg-white text-[color:var(--color-primary)] border-[color:var(--color-gray)]'
                    }`}
                  >
                    {fmtTime(s.startTime)}
                  </button>
                ))}
              </div>
            )}
          </div>
          {errorText && <div className="text-[13px] text-[color:var(--color-primary-danger)]">{errorText}</div>}
          <div className="flex gap-x-3 mt-2 justify-center">
            <Button type="button" loading={saving} onClick={submit} primary>
              Save New Time
            </Button>
            <Button type="button" onClick={onClose} primaryOutline>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RescheduleBookingModal
